/**
 * Single value, with Looker's comparison under it when the author turned one
 * on: the next field as a value, a change (arrow + colour), or progress
 * towards it (bar). The arithmetic lives in lookerChartData.comparisonOf.
 *
 * @module components/looker/charts/SingleValueTile
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LookerTile, LookerTileWidth } from '@/services/looker/lookerTypes';
import { cellText, comparisonOf, toChartNumber } from '../lookerChartData';
import { deltaOf, deltaTone, formatDelta, TONE_CLASS } from '@/services/looker/lookerDelta';

const ARROW = { up: '▲', down: '▼', flat: '■' } as const;

/** From here a sixth card shows 609.3K / 34.6M: a full value does not fit six across. */
const SHORTEN_FROM = 10_000;

/** One decimal, K/M/B. The full value stays on hover and for screen readers. */
const shortNumber = (v: number): string => {
  const abs = Math.abs(v);
  const [div, suffix] = abs >= 1e9 ? [1e9, 'B'] : abs >= 1e6 ? [1e6, 'M'] : [1e3, 'K'];
  return `${(v / div).toLocaleString('en-US', { maximumFractionDigits: 1 })}${suffix}`;
};

/**
 * A sixth is a narrow card (six across at xl): its number is drawn smaller, and
 * a large one is shortened (609.3K) with the full value in the title and for
 * screen readers. It may wrap but never clips.
 */
export const SingleValueTile: React.FC<{ tile: LookerTile; width?: LookerTileWidth }> = ({ tile, width }) => {
  const { t } = useTranslation();
  const f = tile.fields.find((x) => x.kind === 'measure') ?? tile.fields[0];
  const text = f ? cellText(tile.rows[0]?.[f.name]) : null;
  const raw = f ? toChartNumber(tile.rows[0]?.[f.name]?.value ?? null) : null;
  const short = width === 'sixth' && text !== null && raw !== null && Math.abs(raw) >= SHORTEN_FROM ? shortNumber(raw) : null;
  const cmp = comparisonOf(tile);
  return (
    <div className="min-w-0 py-4">
      <p className={`min-w-0 font-bold tracking-tight tabular-nums text-neutral-900 [overflow-wrap:anywhere] ${width === 'sixth' ? 'text-2xl xl:text-3xl' : 'text-4xl'}`}
        title={short ? text ?? undefined : undefined}
      >
        {short ? <><span aria-hidden>{short}</span><span className="sr-only">{text}</span></> : text ?? t('looker.noData')}
      </p>
      {cmp && cmp.type === 'value' && (
        <p className="mt-2 text-sm text-neutral-700">
          {cmp.label && <span>{cmp.label} </span>}
          <span className="font-semibold tabular-nums text-neutral-900">{cmp.text}</span>
        </p>
      )}
      {cmp && cmp.type === 'change' && cmp.direction && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-sm">
          <span
            className={`font-semibold tabular-nums ${cmp.good === null ? 'text-neutral-800' : cmp.good ? 'text-success-700' : 'text-error-700'}`}
          >
            <span aria-hidden>{ARROW[cmp.direction]} </span>
            {cmp.text}
          </span>
          {cmp.label && <span className="text-neutral-700">{cmp.label}</span>}
        </p>
      )}
      {tile.previous !== undefined && f && (() => {
        const d = deltaOf(toChartNumber(tile.rows[0]?.[f.name]?.value ?? null), toChartNumber(tile.previous?.[f.name]?.value ?? null));
        const tone = deltaTone(f.name, d);
        return (
          <div data-testid="looker-delta" className="mt-2 text-sm">
            <span className={`whitespace-nowrap font-semibold tabular-nums ${TONE_CLASS[tone]}`}>
              {d === null ? '—' : <><span aria-hidden>{d > 0 ? '▲' : d < 0 ? '▼' : '■'} </span>{formatDelta(d)}</>}
            </span>
            <span data-testid="looker-delta-label" className="block text-xs text-neutral-700">{t('looker.vsPrevious')}</span>
          </div>
        );
      })()}
      {cmp && (cmp.type === 'progress' || cmp.type === 'progress_percentage') && cmp.progress !== null && (
        <div className="mt-3">
          <div className="h-2 w-full rounded-full bg-neutral-100">
            <div className="h-2 rounded-full bg-[#1a73e8]" style={{ width: `${cmp.progress * 100}%` }} />
          </div>
          <p className="mt-1.5 text-xs tabular-nums text-neutral-700">
            {cmp.type === 'progress_percentage'
              ? t('looker.progressOf', { pct: cmp.percentText, target: cmp.text })
              : cmp.label ? `${cmp.label} ${cmp.text}` : cmp.text}
          </p>
        </div>
      )}
    </div>
  );
};
